import { args, createCommandHandler, createLogger } from "ceyper-common";
import type { File } from "$lib/wasm/file";

export const LOGGER = createLogger<any>();

export let COMMANDS = createCommandHandler(
    {
        /// SCOPES
        none: { name: "None" },
        editor: { name: "Editor" },
        modal: { name: "Modal", blocking: true },
        animation: { name: "Animation" },
    },
    {
        /// CATEGORIES
        general: { name: "General" },
        editor: { name: "Editor" },
        nav: { name: "Navigation" },
        camera: { name: "Camera" },
        file: { name: "File" },
    },
    {
        "general.debug": {
            name: "Debug",
            default: "ctrl+l",
            args: [],
            canRebind: true,
            searchable: true,
        },
        /// COMMANDS
        "general.cancel": {
            name: "Cancel",
            default: "esc",
            canRebind: false,
            ...args<[string]>(),
            searchable: false,
        },
        "general.undo": {
            name: "Undo",
            group: "Edit",
            repeat: true,
            default: "ctrl+z",
            ...args<[number?]>(),
        },
        "general.redo": {
            name: "Redo",
            group: "Edit",
            repeat: true,
            default: "ctrl+shift+z",
            ...args<[number?]>(),
        },

        "general.search": { name: "Search", default: "ctrl+f", args: [], searchable: false },

        "general.settings": { name: "Settings", default: "ctrl+alt+s", args: [] },

        /// NAVIGATION
        "nav.up": {
            name: "Up",
            default: "up, shift+up",
            repeat: true,
            args: [],
            canRebind: false,
            searchable: false,
        },
        "nav.down": {
            name: "Down",
            default: "down, shift+down",
            repeat: true,
            args: [],
            canRebind: false,
            searchable: false,
        },
        "nav.left": {
            name: "Left",
            default: "left, shift+left",
            repeat: true,
            args: [],
            canRebind: false,
            searchable: false,
        },
        "nav.right": {
            name: "Right",
            default: "right, shift+right",
            repeat: true,
            args: [],
            canRebind: false,
            searchable: false,
        },
        "nav.confirm": {
            name: "Confirm",
            default: "enter",
            args: [],
            canRebind: false,
            searchable: false,
        },

        /// CAMERA
        // "camera.reset": { name: "Reset", default: "r", args: [] },
        "camera.pan": {
            name: "Pan",
            group: "Camera",
            default: "mouse-middle, space",
            keydown: true,
            keyup: true,
            args: [],
            searchable: false,
        },
        "camera.zoom-in": {
            name: "Zoom in (at cursor)",
            group: "Camera",
            default: "mouse-scroll-up",
            args: [],
            searchable: false,
        },
        "camera.zoom-out": {
            name: "Zoom out (at cursor)",
            group: "Camera",
            default: "mouse-scroll-down",
            args: [],
            searchable: false,
        },
        "camera.zoom-in-center": {
            name: "Zoom in (at center)",
            group: "Camera",
            default: "=",
            args: [],
            searchable: false,
        },
        "camera.zoom-out-center": {
            name: "Zoom out (at center)",
            group: "Camera",
            default: "-",
            args: [],
            searchable: false,
        },
        "camera.center": { name: "Center", group: "Camera", default: "f", args: [] },

        /// FILE
        "file.new": { name: "New", group: "File", default: "ctrl+n", args: [] },
        "file.open": { name: "Open", group: "File", default: "ctrl+o", args: [] },
        "file.save": { name: "Save", group: "File", default: "ctrl+s", ...args<[boolean?]>() },
        "file.download": { name: "Download", group: "File", default: "", args: [] },
        "file.download-as": { name: "Download as...", group: "File", default: "", args: [] },
        "file.resize": { name: "Resize", group: "File", default: "", ...args<[number, number]>() },
        "file.load": {
            name: "Load file",
            group: "File",
            ...args<[File]>(),
            internal: true,
            searchable: false,
        },
        "file.rename": { name: "Rename file", group: "File", ...args<[string?]>() },
        "file.clear-history": { name: "Delete file history", group: "File", default: "", args: [] },

        /// EDIT
        "edit.set-data": {
            name: "Set data",
            ...args<[Uint8ClampedArray]>(),
            internal: true,
            searchable: false,
        },

        // MODIFY
        "edit.clear": { name: "Clear", group: "Edit", default: "del", args: [] },
        "edit.offset": {
            name: "Offset",
            group: "Edit",
            default: "ctrl+shift+o",
            ...args<[number?, number?]>(),
        },
        "edit.flip-horizontal": { name: "Flip horizontal", group: "Edit", default: "", args: [] },
        "edit.flip-vertical": { name: "Flip vertical", group: "Edit", default: "", args: [] },
        "edit.outline": { name: "Outline", group: "Edit", ...args<[number?, boolean?]>() },
        "edit.copy": { name: "Copy", group: "Edit", default: "ctrl+c", args: [] },
        "edit.paste": { name: "Paste", group: "Edit", default: "ctrl+v", args: [] },

        // LAYERS
        "edit.add-layer": { name: "Add", group: "Layer", default: "ctrl+shift+n", args: [] },
        "edit.remove-layer": {
            name: "Remove",
            group: "Layer",
            default: "ctrl+shift+del",
            ...args<[number]>(),
        },
        "edit.move-layer-up": {
            name: "Move up",
            group: "Layer",
            default: "ctrl+shift+up",
            args: [],
        },
        "edit.move-layer-down": {
            name: "Move down",
            group: "Layer",
            default: "ctrl+shift+down",
            args: [],
        },
        "edit.duplicate-layer": {
            name: "Duplicate",
            group: "Layer",
            default: "ctrl+shift+d",
            args: [],
        },
        "edit.move-layer-down-index": {
            name: "Move down",
            group: "Layer",
            ...args<[number]>(),
            internal: true,
            searchable: false,
        },
        "edit.move-layer-up-index": {
            name: "Move up",
            group: "Layer",
            ...args<[number]>(),
            internal: true,
            searchable: false,
        },
        "edit.merge-layers": {
            name: "Merge down",
            group: "Layer",
            default: "ctrl+shift+m",
            args: [],
        },
        "edit.rename-layer": { name: "Rename", group: "Layer", default: "ctrl+shift+r", args: [] },

        "edit.toggle-layer-visibility": {
            name: "Toggle visibility",
            group: "Layer",
            default: "ctrl+shift+v",
            ...args<[number?, boolean?]>(),
        },
        "edit.toggle-layer-focus": {
            name: "Toggle focus mode",
            group: "Layer",
            default: "ctrl+shift+alt+v",
            args: [],
        },
        "edit.toggle-view-tiling": {
            name: "Toggle tiling",
            group: "Layer",
            default: "ctrl+shift+alt+t",
            args: [],
        },

        "edit.select-layer-up": { name: "Select up", group: "Layer", default: "ctrl+up", args: [] },
        "edit.select-layer-down": {
            name: "Select down",
            group: "Layer",
            default: "ctrl+down",
            args: [],
        },

        // Animation
        "animation.load": {
            name: "Load",
            group: "Animation",
            useKeyboard: false,
            ...args<[number]>(),
            internal: true,
            searchable: false,
        },
        "animation.change-frame": {
            name: "Change",
            group: "Frame",
            useKeyboard: false,
            ...args<[number]>(),
            internal: true,
            searchable: false,
        },
        "animation.first-frame": { name: "First", group: "Frame", args: [] },
        "animation.prev-frame": {
            name: "Previous",
            group: "Frame",
            args: [],
            default: "ctrl+left",
        },
        "animation.next-frame": {
            name: "Next",
            group: "Frame",
            args: [],
            default: "ctrl+right",
        },
        "animation.last-frame": { name: "Last", group: "Frame", args: [] },
        "animation.add-frame": { name: "Add", group: "Frame", ...args<[number?]>() },
        "animation.duplicate-frame": { name: "Duplicate", group: "Frame", ...args<[number?]>() },
        "animation.remove-frame": {
            name: "Remove",
            group: "Frame",
            ...args<[number?]>(),
        },
        "animation.move-frame": {
            name: "Move",
            group: "Frame",
            useKeyboard: false,
            ...args<[number, number, boolean?]>(),
            internal: true,
            searchable: false,
        },
        "animation.move-frame-left": {
            name: "Move left",
            group: "Frame",
            args: [],
            default: "ctrl+shift+left",
        },

        "animation.move-frame-right": {
            name: "Move right",
            group: "Frame",
            args: [],
            default: "ctrl+shift+right",
        },
        "animation.play": {
            name: "Play animation",
            group: "Animation",
            args: [],
        },
        "animation.pause": {
            name: "Pause animation",
            group: "Animation",
            args: [],
        },
        "animation.toggle-playback": {
            name: "Toggle animation playback",
            group: "Animation",
            args: [],
            default: "ctrl+space",
        },
        "animation.toggle-loop": {
            name: "Toggle animation loop",
            group: "Animation",
            args: [],
        },
        "animation.set-loop": {
            name: "Set animation loop",
            group: "Animation",
            ...args<[boolean]>(),
            internal: true,
            searchable: false,
        },
        "animation.set-frame-duration": {
            name: "Set frame duration",
            group: "Frame",
            ...args<[number?, number?]>(),
        },
        "animation.set-all-frame-durations": {
            name: "Set all frame durations",
            group: "Frame",
            ...args<[number]>(),
        },

        /// EDITOR
        /// TOOLS
        "editor.tool.cancel": { name: "Cancel", group: "Tool", default: "esc", args: [] },
        "editor.tool.pencil": { name: "Pencil", group: "Tool", default: "q", args: [] },
        "editor.tool.eraser": { name: "Eraser", group: "Tool", default: "e", args: [] },
        "editor.tool.picker": { name: "Color picker", group: "Tool", default: "t", args: [] },
        "editor.tool.picker-once": {
            name: "Color picker (temporary)",
            group: "Tool",
            default: "alt+mouse-left",
            args: [],
            canRebind: false,
            searchable: false,
            keydown: true,
            keyup: true,
        },
        "editor.tool.move": { name: "Move", group: "Tool", default: "m", args: [] },

        "editor.tool.fill-group": { name: "Fill s", group: "Tool", default: "g", args: [] },
        "editor.tool.fill": { name: "Fill", group: "Tool", default: "g", args: [] },
        "editor.tool.gradient": { name: "Gradient", group: "Tool", default: "h", args: [] },

        // "editor.tool.shape-group": { name: "Shape", group: "Shape", default: "s", args: [] },
        "editor.tool.ellipse": { name: "Ellipse", group: "Shape", args: [] },
        "editor.tool.rectangle": { name: "Rectangle", group: "Shape", args: [] },

        "editor.tool.line": { name: "Line", group: "Shape", default: "l", args: [] },

        "editor.tool.box-select": {
            name: "Box select",
            group: "Selection",
            default: "b",
            args: [],
        },
        "editor.tool.wand-select": {
            name: "Wand select",
            group: "Selection",
            args: [],
        },
        "editor.tool.lasso-select": {
            name: "Lasso select",
            group: "Selection",
            args: [],
        },
        "editor.tool.select-all": {
            name: "Select all",
            group: "Selection",
            default: "ctrl+a",
            args: [],
        },
        "editor.tool.clear-selection": {
            name: "Clear selection",
            group: "Selection",
            default: "esc, ctrl+d",
            args: [],
        },
        "editor.tool.invert-selection": {
            name: "Invert selection",
            group: "Selection",
            default: "ctrl+shift+i",
            args: [],
        },

        "editor.reference-edit": { name: "Edit reference", args: [] },
        "editor.reference-change": { name: "Change reference", args: [] },

        // Palette
        "editor.tool.set-color": {
            name: "Set color",
            group: "Palette",
            useKeyboard: false,
            ...args<[Color]>(),
            internal: true,
            searchable: false,
        },
        "editor.tool.palette-index": {
            name: "Set palette color",
            group: "Palette",
            useKeyboard: false,
            ...args<[number]>(),
            internal: true,
            searchable: false,
        },
        "editor.tool.palette-prev": {
            name: "Previous palette color",
            group: "Palette",
            default: "shift+mouse-scroll-up, [",
            repeat: true,
            args: [],
        },
        "editor.tool.palette-next": {
            name: "Next palette color",
            group: "Palette",
            default: "shift+mouse-scroll-down, ]",
            repeat: true,
            args: [],
        },

        /// PANELS
        "view.palette": { name: "Palette", group: "Panel", args: [] },
        "view.layers": { name: "Layers", group: "Panel", args: [] },
        "view.files": { name: "Files", group: "Panel", args: [] },
        "view.history": { name: "History", group: "Panel", args: [] },
        "view.reset-layout": { name: "Reset layout", group: "Panel", args: [] },
        "view.tool-options": { name: "Tool options", group: "Panel", args: [] },
        "view.color-picker": { name: "Color picker", group: "Panel", args: [] },
    },
    {} as { searchable?: boolean }
);

LOGGER.addLevel("COMMAND", "#5566BB");
COMMANDS.events.subscribe((data: any) => {
    if (data.type === "error") {
        LOGGER.error(data.data.message, data.data.args);
    } else {
        LOGGER.log("COMMAND", data.type + " {0}", {
            data: (data as any).data,
            toString() {
                return JSON.stringify(this.data);
            },
        });
    }
});
